/**
 * プロフィールフィールド編集モーダル
 *
 * 各フィールド（好きな色、好きな動物など）を個別に編集
 */
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { AppDispatch } from "app/store";
import { useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition, Select } from "common/elements";
import I18n from "features/shared/i18n";
import { updateUser } from "features/player/api";
import {
  HAIRSTYLE_OPTIONS,
  BODY_TYPE_OPTIONS,
  FACE_SHAPE_OPTIONS,
  COLOR_PALETTE,
  generateColorVariations,
  BaseColorKey,
} from "features/user/constants";

// 保護者ページ用の外見オプション（漢字バージョン）
const PARENT_HAIRSTYLE_OPTIONS = [
  { key: "very_short", icon: "face", labelKey: "MSG_HAIRSTYLE_VERY_SHORT_BY_PARENT" },
  { key: "short", icon: "face", labelKey: "MSG_HAIRSTYLE_SHORT_BY_PARENT" },
  { key: "medium", icon: "face_3", labelKey: "MSG_HAIRSTYLE_MEDIUM_BY_PARENT" },
  { key: "slightly_long", icon: "face_4", labelKey: "MSG_HAIRSTYLE_SLIGHTLY_LONG_BY_PARENT" },
  { key: "long", icon: "face_4", labelKey: "MSG_HAIRSTYLE_LONG_BY_PARENT" },
];

const PARENT_BODY_TYPE_OPTIONS = [
  { key: "thin", icon: "accessibility_new", labelKey: "MSG_BODY_TYPE_THIN_BY_PARENT" },
  {
    key: "slightly_thin",
    icon: "accessibility_new",
    labelKey: "MSG_BODY_TYPE_SLIGHTLY_THIN_BY_PARENT",
  },
  { key: "medium", icon: "accessibility", labelKey: "MSG_BODY_TYPE_MEDIUM_BY_PARENT" },
  {
    key: "slightly_chubby",
    icon: "accessibility",
    labelKey: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY_BY_PARENT",
  },
  { key: "chubby", icon: "accessibility", labelKey: "MSG_BODY_TYPE_CHUBBY_BY_PARENT" },
];

const PARENT_FACE_SHAPE_OPTIONS = [
  { key: "round", icon: "circle", labelKey: "MSG_FACE_SHAPE_ROUND_BY_PARENT" },
  { key: "oval", icon: "egg", labelKey: "MSG_FACE_SHAPE_OVAL_BY_PARENT" },
  { key: "oblong", icon: "rectangle", labelKey: "MSG_FACE_SHAPE_OBLONG_BY_PARENT" },
  { key: "square", icon: "square", labelKey: "MSG_FACE_SHAPE_SQUARE_BY_PARENT" },
  { key: "triangle", icon: "change_history", labelKey: "MSG_FACE_SHAPE_TRIANGLE_BY_PARENT" },
];

// 保護者ページ用の色オプション（漢字バージョン）
const PARENT_COLOR_PALETTE: Array<{
  key: BaseColorKey;
  labelKey: string;
  hex: string;
}> = [
  { key: "red", labelKey: "MSG_COLOR_RED_BY_PARENT", hex: "#EF4444" },
  { key: "blue", labelKey: "MSG_COLOR_BLUE_BY_PARENT", hex: "#3B82F6" },
  { key: "yellow", labelKey: "MSG_COLOR_YELLOW_BY_PARENT", hex: "#EAB308" },
  { key: "green", labelKey: "MSG_COLOR_GREEN_BY_PARENT", hex: "#22C55E" },
  { key: "white", labelKey: "MSG_COLOR_WHITE_BY_PARENT", hex: "#FFFFFF" },
];

// 保護者ページ用の得意教科オプション（漢字バージョン）
const PARENT_BEST_SUBJECT_OPTIONS = [
  { key: "japanese", icon: "menu_book", labelKey: "MSG_SUBJECT_LANGUAGE_BY_PARENT" },
  { key: "math", icon: "calculate", labelKey: "MSG_SUBJECT_MATH_BY_PARENT" },
  { key: "science", icon: "science", labelKey: "MSG_SUBJECT_SCIENCE_BY_PARENT" },
  { key: "social", icon: "public", labelKey: "MSG_SUBJECT_SOCIAL_BY_PARENT" },
  { key: "music", icon: "music_note", labelKey: "MSG_SUBJECT_MUSIC_BY_PARENT" },
  { key: "art", icon: "palette", labelKey: "MSG_SUBJECT_ART_BY_PARENT" },
  { key: "pe", icon: "sports_soccer", labelKey: "MSG_SUBJECT_PE_BY_PARENT" },
  { key: "life", icon: "eco", labelKey: "MSG_SUBJECT_LIFE_BY_PARENT" },
  { key: "home_ec", icon: "restaurant", labelKey: "MSG_SUBJECT_HOME_EC_BY_PARENT" },
  { key: "ethics", icon: "volunteer_activism", labelKey: "MSG_SUBJECT_ETHICS_BY_PARENT" },
  { key: "english", icon: "translate", labelKey: "MSG_SUBJECT_ENGLISH_BY_PARENT" },
  { key: "integrated", icon: "hub", labelKey: "MSG_SUBJECT_INTEGRATED_BY_PARENT" },
  {
    key: "special_activities",
    icon: "groups",
    labelKey: "MSG_SUBJECT_SPECIAL_ACTIVITIES_BY_PARENT",
  },
];

export enum ProfileFieldType {
  FAVORITE_COLOR = "favorite_color",
  BEST_SUBJECT = "best_subject",
  SELF_INTRODUCTION = "self_introduction",
  HAIRSTYLE = "hairstyle",
  BODY_TYPE = "body_type",
  FACE_SHAPE = "face_shape",
}

interface ProfileFieldEditModalProps {
  open: boolean;
  uid: string;
  fieldType: ProfileFieldType;
  currentValue: string[] | string | null;
  onClose: () => void;
}

const getFieldTitle = (fieldType: ProfileFieldType): string => {
  switch (fieldType) {
    case ProfileFieldType.FAVORITE_COLOR:
      return I18n.t("MSG_PARENT_PAGE_EDIT_FAVORITE_COLOR");
    case ProfileFieldType.BEST_SUBJECT:
      return I18n.t("MSG_PARENT_PAGE_EDIT_BEST_SUBJECT");
    case ProfileFieldType.SELF_INTRODUCTION:
      return I18n.t("MSG_PARENT_PAGE_EDIT_SELF_INTRO");
    case ProfileFieldType.HAIRSTYLE:
      return I18n.t("MSG_PARENT_PAGE_EDIT_HAIRSTYLE");
    case ProfileFieldType.BODY_TYPE:
      return I18n.t("MSG_PARENT_PAGE_EDIT_BODY_TYPE");
    case ProfileFieldType.FACE_SHAPE:
      return I18n.t("MSG_PARENT_PAGE_EDIT_FACE_SHAPE");
    default:
      return "";
  }
};

const getFieldIcon = (fieldType: ProfileFieldType): string => {
  switch (fieldType) {
    case ProfileFieldType.FAVORITE_COLOR:
      return "palette";
    case ProfileFieldType.BEST_SUBJECT:
      return "school";
    case ProfileFieldType.SELF_INTRODUCTION:
      return "person";
    case ProfileFieldType.HAIRSTYLE:
      return "face";
    case ProfileFieldType.BODY_TYPE:
      return "accessibility";
    case ProfileFieldType.FACE_SHAPE:
      return "face_retouching_natural";
    default:
      return "edit";
  }
};

export const ProfileFieldEditModal = ({
  open,
  uid,
  fieldType,
  currentValue,
  onClose,
}: ProfileFieldEditModalProps) => {
  const { width, scale } = useScale();
  // 狭い画面（scale下限クランプ時）でも収まるよう、設計幅を上限に可変にする
  const modalWidth = Math.min(600, width / scale - 16);
  const isShrunk = modalWidth < 600;
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [textValue, setTextValue] = useState("");
  const [disable, setDisable] = useState(false);
  const [tempBaseColor, setTempBaseColor] = useState<BaseColorKey | null>(null);
  const [tempVariationColor, setTempVariationColor] = useState<string | null>(null);

  // 外見用Selectの開閉状態とref
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = useRef<HTMLButtonElement>(null);

  // フィールドタイプに応じたオプションを取得
  const getOptions = () => {
    switch (fieldType) {
      case ProfileFieldType.BEST_SUBJECT:
        return PARENT_BEST_SUBJECT_OPTIONS;
      case ProfileFieldType.HAIRSTYLE:
        return HAIRSTYLE_OPTIONS;
      case ProfileFieldType.BODY_TYPE:
        return BODY_TYPE_OPTIONS;
      case ProfileFieldType.FACE_SHAPE:
        return FACE_SHAPE_OPTIONS;
      default:
        return [];
    }
  };

  // 単一選択フィールドかどうか
  const isSingleSelect =
    fieldType === ProfileFieldType.HAIRSTYLE ||
    fieldType === ProfileFieldType.BODY_TYPE ||
    fieldType === ProfileFieldType.FACE_SHAPE;

  // バリデーション：値が設定されているかどうか
  const isValid = (): boolean => {
    switch (fieldType) {
      case ProfileFieldType.FAVORITE_COLOR:
        return !!tempVariationColor;
      case ProfileFieldType.SELF_INTRODUCTION:
        return true; // 自己紹介は任意なので空でもOK
      default:
        return selectedItems.length > 0;
    }
  };

  useEffect(() => {
    if (open) {
      setDisable(false);

      if (fieldType === ProfileFieldType.SELF_INTRODUCTION) {
        setTextValue((currentValue as string) || "");
      } else if (fieldType === ProfileFieldType.FAVORITE_COLOR) {
        // 好きな色の初期化（ベース色+バリエーション形式）
        const values = (currentValue as string[]) || [];
        const savedColor = values[0] || null;

        if (savedColor) {
          // 保存されている色からベース色を特定
          let foundBase: BaseColorKey | null = null;
          let foundVariation: string | null = null;

          for (const palette of COLOR_PALETTE) {
            // ベース色キーと一致する場合（旧形式）
            if (savedColor === palette.key) {
              foundBase = palette.key;
              const variations = generateColorVariations(palette.key);
              foundVariation = variations[2]; // 中央のバリエーション
              break;
            }
            // バリエーションに含まれる場合
            const variations = generateColorVariations(palette.key);
            if (variations.map((v) => v.toUpperCase()).includes(savedColor.toUpperCase())) {
              foundBase = palette.key;
              foundVariation = savedColor;
              break;
            }
          }

          setTempBaseColor(foundBase);
          setTempVariationColor(foundVariation);
        } else {
          setTempBaseColor(null);
          setTempVariationColor(null);
        }
      } else if (isSingleSelect) {
        // 単一選択の場合
        const value = currentValue as string;
        setSelectedItems(value ? [value] : []);
      } else {
        // 複数選択の場合（得意な教科）
        const options = getOptions();
        const optionKeys = options.map((o) => o.key);
        const values = (currentValue as string[]) || [];

        setSelectedItems(values.filter((v) => optionKeys.includes(v)));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fieldType, currentValue]);

  const handleItemSelect = (key: string) => {
    if (isSingleSelect) {
      // 単一選択の場合は選択を切り替え
      setSelectedItems([key]);
    } else {
      // 複数選択の場合はトグル
      if (selectedItems.includes(key)) {
        setSelectedItems(selectedItems.filter((k) => k !== key));
      } else {
        setSelectedItems([...selectedItems, key]);
      }
    }
  };

  const handleConfirm = () => {
    if (disable) return;
    setDisable(true);

    handleDialog({ type: DialogType.LOADING, value: true });

    const userInfo: Record<string, string[] | string | null> = {};

    if (fieldType === ProfileFieldType.SELF_INTRODUCTION) {
      userInfo.self_introduction = textValue || null;
    } else if (fieldType === ProfileFieldType.FAVORITE_COLOR) {
      // 好きな色は単一のHex値を配列として保存
      userInfo.favorite_color = tempVariationColor ? [tempVariationColor] : null;
    } else if (isSingleSelect) {
      // 単一選択の場合は文字列
      userInfo[fieldType] = selectedItems[0] || null;
    } else {
      // 複数選択の場合は配列（得意な教科）
      userInfo[fieldType] = selectedItems.length > 0 ? selectedItems : null;
    }

    dispatch(updateUser({ uid, userInfo }))
      .unwrap()
      .then(() => {
        toast(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
          </div>,
        );
        onClose();
      })
      .catch((error) => {
        console.error(error);
        setDisable(false);
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  const handleBaseColorSelect = (key: BaseColorKey) => {
    setTempBaseColor(key);
    // バリエーションをリセット
    setTempVariationColor(null);
  };

  const handleVariationSelect = (hex: string) => {
    setTempVariationColor(hex);
  };

  const renderColorSelection = () => {
    const variations = tempBaseColor ? generateColorVariations(tempBaseColor) : [];
    return (
      <div className="flex-col-el items-center gap-6">
        {/* 上部：ベース色選択（5つの円形ボタン、狭い画面では折り返す） */}
        <div className="flex-row-el flex-center flex-wrap gap-5">
          {PARENT_COLOR_PALETTE.map((option) => {
            const isActive = tempBaseColor === option.key;
            const isWhite = option.key === "white";
            return (
              <div
                key={option.key}
                onClick={() => handleBaseColorSelect(option.key)}
                className="flex-col-el flex-center gap-2 cursor-pointer select-none"
              >
                <div
                  className={`
                    flex-col-el flex-center
                    w-[80px] h-[80px] rounded-full active:scale-95
                    ${isActive ? "ring-4 ring-green-100" : ""}
                    ${isWhite ? "border-2 border-gray-40" : ""}
                  `}
                  style={{ backgroundColor: option.hex }}
                >
                  {isActive && (
                    <span className="material-symbols-outlined text-[32px] text-white drop-shadow-md">
                      check
                    </span>
                  )}
                </div>
                <span
                  className={`text-body text-[16px] ${isActive ? "text-green-100" : "text-textcolor-black/70"}`}
                >
                  {I18n.t(option.labelKey)}
                </span>
              </div>
            );
          })}
        </div>

        {/* 下部：バリエーション選択 */}
        <div className="flex-col-el items-center gap-4">
          <p className="text-body text-textcolor-black text-[18px]">
            {I18n.t("MSG_COLOR_SELECT_SHADE")}
          </p>
          <div className="flex-row-el gap-4">
            {tempBaseColor
              ? variations.map((hex, idx) => {
                  const isSelected = tempVariationColor === hex;
                  const isWhiteVariation = tempBaseColor === "white";
                  return (
                    <div
                      key={idx}
                      onClick={() => handleVariationSelect(hex)}
                      className={`
                        flex-col-el flex-center w-[60px] h-[60px] rounded-full
                        cursor-pointer select-none active:scale-95
                        ${isSelected ? "ring-4 ring-green-100" : ""}
                        ${isWhiteVariation ? "border-2 border-gray-40" : ""}
                      `}
                      style={{ backgroundColor: hex }}
                    >
                      {isSelected && (
                        <span className="material-symbols-outlined text-[24px] text-white drop-shadow-md">
                          check
                        </span>
                      )}
                    </div>
                  );
                })
              : // プレースホルダー（5つの空の円）
                Array.from({ length: 5 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="flex-col-el flex-center w-[60px] h-[60px] rounded-full border-2 border-dashed border-gray-40"
                  />
                ))}
          </div>
        </div>
      </div>
    );
  };

  const renderIconSelection = (options: { key: string; icon: string; labelKey: string }[]) => (
    <div className="flex-col-el items-center">
      <div
        className={
          isShrunk
            ? "flex-row-el flex-wrap justify-center gap-3 w-full"
            : "grid grid-cols-5 gap-3 justify-items-center"
        }
      >
        {options.map((option) => {
          const isActive = selectedItems.includes(option.key);
          return (
            <div
              key={option.key}
              onClick={() => handleItemSelect(option.key)}
              className={`flex-col-el flex-center relative w-[90px] h-[90px] rounded-[8px] cursor-pointer select-none hover:opacity-90 ${isActive ? "bg-green-alt border-2 border-green-100" : "bg-surface border-2 border-gray2-40"}`}
            >
              {isActive && (
                <span className="absolute top-1 right-1 material-symbols-outlined text-[14px] text-green-100">
                  check_circle
                </span>
              )}
              <span
                className={`material-symbols-outlined !text-[28px] mb-1 ${isActive ? "text-green-100" : "text-gray2-80"}`}
              >
                {option.icon}
              </span>
              <span
                className={`text-body text-[12px] ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
              >
                {I18n.t(option.labelKey)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderTextArea = () => (
    <div className="flex-col-el items-center w-full">
      <div className="flex-col-el w-full max-w-[520px]">
        <textarea
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          placeholder={I18n.t("MSG_PROFILE_AI_OTHER_INFO_PLACEHOLDER_BY_PARENT")}
          maxLength={200}
          rows={5}
          className="input-modal text-body text-textcolor-black text-[18px] resize-none"
        />
        <p className="text-body text-right text-[14px] text-gray2-80 mt-2">
          {textValue.length}/200
        </p>
      </div>
    </div>
  );

  // 外見用Selectドロップダウン（UserProfileと同じUI）
  const renderSelectField = (options: { key: string; icon: string; labelKey: string }[]) => {
    const currentValue = selectedItems[0] || "";
    const currentLabel = options.find((o) => o.key === currentValue)?.labelKey;

    return (
      <div className="flex-col-el items-center w-full">
        <div className="flex-col-el w-full max-w-[400px]">
          <button
            ref={selectRef}
            onClick={() => {
              setSelectOpen(true);
            }}
            className="flex-row-el items-center justify-between w-full py-4 px-6 bg-white border-2 border-green-alt rounded-[12px] cursor-pointer hover:bg-green-alt/10 transition-all"
          >
            <span className="text-body text-textcolor-black text-[20px]">
              {currentLabel ? I18n.t(currentLabel) : I18n.t("MSG_SELECT_PLACEHOLDER_BY_PARENT")}
            </span>
            <span className="material-symbols-outlined !text-[24px] text-green-100">
              expand_more
            </span>
          </button>
          <Select
            anchorRef={selectRef}
            open={selectOpen}
            onClose={() => setSelectOpen(false)}
            value={currentValue}
            onChange={(v: string) => {
              setSelectedItems([v]);
              setSelectOpen(false);
            }}
            options={options.map((o) => ({ value: o.key, label: I18n.t(o.labelKey) }))}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (fieldType) {
      case ProfileFieldType.FAVORITE_COLOR:
        return renderColorSelection();
      case ProfileFieldType.BEST_SUBJECT:
        return renderIconSelection(PARENT_BEST_SUBJECT_OPTIONS);
      case ProfileFieldType.SELF_INTRODUCTION:
        return renderTextArea();
      case ProfileFieldType.HAIRSTYLE:
        return renderSelectField(PARENT_HAIRSTYLE_OPTIONS);
      case ProfileFieldType.BODY_TYPE:
        return renderSelectField(PARENT_BODY_TYPE_OPTIONS);
      case ProfileFieldType.FACE_SHAPE:
        return renderSelectField(PARENT_FACE_SHAPE_OPTIONS);
      default:
        return null;
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div style={{ width: modalWidth }} className="flex-col-el border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              {/* ヘッダー */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">
                  {getFieldIcon(fieldType)}
                </span>
                <h2 className="text-[33px] text-textcolor-black">{getFieldTitle(fieldType)}</h2>
              </div>

              {/* コンテンツ */}
              <div className="flex-col-el py-4 min-h-[350px]">{renderContent()}</div>

              {/* ボタン */}
              <div className="flex-row-el flex-center gap-4">
                <button
                  onClick={onClose}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={disable || !isValid()}
                  className={`flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] transition-all ${
                    disable || !isValid()
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:opacity-90"
                  }`}
                >
                  {I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
